import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react';
import { ThemeProvider as MuiThemeProvider, createTheme } from '@mui/material/styles';
import { AuthContext } from '_store';
import {
    THEME_STORAGE_KEY,
    applyDocumentTheme,
    cacheColorScheme,
    normalizeColorScheme,
    readCachedColorScheme,
    resolveTheme,
} from './theme';

const ThemeContext = createContext({
    colorScheme: 'system',
    resolvedTheme: 'light',
    setColorScheme: () => {},
});

const darkMediaQuery = () => (
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
        ? window.matchMedia('(prefers-color-scheme: dark)')
        : null
);

function AppThemeProvider({ children }) {
    const { authUser } = useContext(AuthContext);
    const [colorScheme, setColorSchemeState] = useState(readCachedColorScheme);
    const [prefersDark, setPrefersDark] = useState(() => !!darkMediaQuery()?.matches);

    const setColorScheme = useCallback((value) => {
        const normalized = cacheColorScheme(value);
        setColorSchemeState(normalized);
    }, []);

    useEffect(() => {
        const accountScheme = authUser?.color_scheme ?? authUser?.colorScheme;
        if (accountScheme) {
            setColorScheme(accountScheme);
        }
    }, [authUser?.colorScheme, authUser?.color_scheme, setColorScheme]);

    useEffect(() => {
        const media = darkMediaQuery();
        if (!media) return undefined;
        const handleChange = (event) => setPrefersDark(event.matches);
        setPrefersDark(media.matches);
        media.addEventListener?.('change', handleChange);
        return () => media.removeEventListener?.('change', handleChange);
    }, []);

    useEffect(() => {
        const handleStorage = (event) => {
            if (event.key === THEME_STORAGE_KEY) {
                setColorSchemeState(normalizeColorScheme(event.newValue));
            }
        };
        window.addEventListener('storage', handleStorage);
        return () => window.removeEventListener('storage', handleStorage);
    }, []);

    const resolvedTheme = resolveTheme(colorScheme, prefersDark);

    useEffect(() => {
        applyDocumentTheme(resolvedTheme);
    }, [resolvedTheme]);

    const muiTheme = useMemo(() => createTheme({
        palette: {
            mode: resolvedTheme,
            primary: { main: resolvedTheme === 'dark' ? '#5fcad7' : '#076873' },
            secondary: { main: resolvedTheme === 'dark' ? '#aeb9bc' : '#47464c' },
            error: { main: resolvedTheme === 'dark' ? '#ffb4ab' : '#ba1a1a' },
            background: {
                default: resolvedTheme === 'dark' ? '#0f1517' : '#f6fafb',
                paper: resolvedTheme === 'dark' ? '#182124' : '#ffffff',
            },
            text: {
                primary: resolvedTheme === 'dark' ? '#f2f7f8' : '#181c1d',
                secondary: resolvedTheme === 'dark' ? '#aeb9bc' : '#47464c',
            },
        },
        typography: {
            fontFamily: 'var(--app-font-stack)',
        },
        shape: {
            borderRadius: 8,
        },
    }), [resolvedTheme]);

    const value = useMemo(() => ({
        colorScheme,
        resolvedTheme,
        setColorScheme,
    }), [colorScheme, resolvedTheme, setColorScheme]);

    return (
        <ThemeContext.Provider value={value}>
            <MuiThemeProvider theme={muiTheme}>{children}</MuiThemeProvider>
        </ThemeContext.Provider>
    );
}

export { AppThemeProvider, ThemeContext };
